import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import quoteService, { type QuoteMilesLeg } from '../../../services/quote.service';

interface Props {
  open: boolean;
  ratezone: string;
  deliveryzone: string;
  zip?: string;
  city?: string;
  state?: string;
  onClose: () => void;
}

/**
 * "Show Miles" breakdown (V1 inline_quote.pl getmiles_Google modal). Fetches the
 * round-trip leg breakdown for the current rate-zone origin → delivery → return
 * and lists each leg's miles + running total. Portaled to <body> so it escapes
 * the editor drawer's transform.
 */
const ShowMilesModal: React.FC<Props> = ({ open, ratezone, deliveryzone, zip, city, state, onClose }) => {
  const [legs, setLegs] = useState<QuoteMilesLeg[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLegs([]);
    quoteService.calculateQuoteMiles({ ratezone, deliveryzone, zip, city, state })
      .then((r) => {
        if (cancelled) return;
        if (r.error) setError(r.error);
        else setLegs(r.legs ?? []);
      })
      .catch(() => { if (!cancelled) setError('Could not calculate miles for this route.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ratezone, deliveryzone, zip, city, state]);

  // Read-only mileage readout: Alt+C closes, and the layer keeps Alt+S off the
  // quote editor underneath.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;
  const th = 'px-3 py-1.5 text-left';
  const td = 'px-3 py-1.5';
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 p-6"
      role="dialog"
      aria-label="Show Miles"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[80vh] w-full max-w-[640px] flex-col rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title="Miles" onClose={onClose} dragHandleProps={dragHandleProps} />
        <div className="overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center text-[0.78125rem] text-slate-400">Calculating…</div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-[0.78125rem] text-red-600 dark:text-red-400">{error}</div>
          ) : legs.length === 0 ? (
            <div className="px-4 py-8 text-center text-[0.78125rem] text-slate-400 dark:text-slate-500">No mileage to show.</div>
          ) : (
            <table className="w-full text-[0.78125rem]">
              <thead className="bg-slate-50 dark:bg-slate-700/60 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className={th}></th>
                  <th className={th}>Origin</th>
                  <th className={th}>Destination</th>
                  <th className={`${th} text-right`}>Miles</th>
                  <th className={`${th} text-right`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {legs.map((leg, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                    <td className={`${td} text-slate-500`}>{i + 1}</td>
                    <td className={td}>{leg.origin}</td>
                    <td className={td}>{leg.destination}</td>
                    <td className={`${td} text-right tabular-nums`}>{leg.miles.toFixed(2)}</td>
                    <td className={`${td} text-right tabular-nums font-semibold`}>{leg.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex justify-center border-t border-slate-200 px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 bg-white px-4 py-1.5 text-[0.78125rem] font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ShowMilesModal;
